import { CreditRequest, creditsClient } from '../client/creditsClient';
import { invalidateOrgCredits } from './useOrgCredits';

/**
 * What every mounted "Request more credits" control shares, kept at module
 * level because the controls (the balance popover, the Step 2 picker, the
 * refused card) do not share a parent: one set of subscribers, one pair of
 * window listeners however many controls are on screen, one read in flight at
 * a time per org, and one place that notices an answer arriving.
 *
 * An answer arrives while the buyer is elsewhere (a platform admin grants it
 * from Backstage and an email says so), so the open request is re-read when
 * the window regains focus or the tab becomes visible again, not only on
 * mount. The request going from open to not open means a platform admin
 * answered; a grant moves the balance and the Step 2 gate is derived from the
 * balance, so the cached balance is invalidated once, here, rather than once
 * per control.
 */

type Listener = () => void;
const listeners = new Set<Listener>();

/** Re-read the open request in every mounted control. A no-op when none is mounted. */
export const refreshCreditRequest = (): void => {
    listeners.forEach((listener) => listener());
};

const onWake = () => {
    // A tab going to the background fires visibilitychange too; nothing to read then.
    if (document.visibilityState === 'hidden') return;
    refreshCreditRequest();
};

/** Subscribe a control; the window listeners exist while at least one is subscribed, and only once. */
export const subscribeCreditRequest = (listener: Listener): (() => void) => {
    if (listeners.size === 0) {
        window.addEventListener('focus', onWake);
        document.addEventListener('visibilitychange', onWake);
    }
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
            window.removeEventListener('focus', onWake);
            document.removeEventListener('visibilitychange', onWake);
        }
    };
};

/** Per org: the id of the open request the last read (or request) saw, or null. */
const lastOpen = new Map<string, string | null>();

const noteOpen = (orgId: string, open: string | null): void => {
    const before = lastOpen.get(orgId);
    lastOpen.set(orgId, open);
    if (before && !open) invalidateOrgCredits();
};

/**
 * Bumped when this browser makes a request. A read that started before it may
 * have missed the request, so its answer must neither be shared with later
 * readers nor be taken for "the request was answered".
 */
const generation = new Map<string, number>();
const generationOf = (orgId: string): number => generation.get(orgId) ?? 0;

const inFlight = new Map<string, Promise<CreditRequest | null>>();

/** This control just made a request: remember it, so its being answered is noticed later. */
export const noteCreditRequestMade = (orgId: string, request: CreditRequest): void => {
    generation.set(orgId, generationOf(orgId) + 1);
    inFlight.delete(orgId);
    lastOpen.set(orgId, request.id);
};

/**
 * The org's newest PENDING or DECLINED request, or null. Controls reading at
 * the same moment (a focus fires for all of them at once) share one request.
 */
export const readCurrentCreditRequest = (orgId: string): Promise<CreditRequest | null> => {
    const shared = inFlight.get(orgId);
    if (shared) return shared;

    const startedAt = generationOf(orgId);
    const read: Promise<CreditRequest | null> = creditsClient
        .getCurrentCreditRequest(orgId)
        .then((current) => {
            if (startedAt === generationOf(orgId)) noteOpen(orgId, current?.status === 'PENDING' ? current.id : null);
            return current;
        })
        .finally(() => {
            if (inFlight.get(orgId) === read) inFlight.delete(orgId);
        });
    inFlight.set(orgId, read);
    return read;
};
